import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';
import { PageMasthead } from '@/components/ui/PageMasthead';
import { PageNav } from '@/components/ui/PageNav';
import { MobileSectionNav } from '@/components/ui/MobileSectionNav';
import { useSectionSpy } from '@/hooks/useSectionSpy';

const FAQ_ITEMS = [
  {
    category: 'Scope & Fit',
    questions: [
      {
        q: 'What exactly is OnPoint Diagnostics?',
        a: 'OnPoint Diagnostics is a bounded, file-based engagement that classifies structural distortion inside one CX workforce system environment. We use your interval-level exports to determine what is structurally true (primary distortion domain, interaction drivers, signal structure) so internal WFM can intervene at the correct layer.',
      },
      {
        q: 'What problems is this designed for?',
        a: 'Recurring instability where numbers look acceptable but outcomes do not. Examples include SLA misses despite enough headcount, occupancy and SLA inversion, forecast accuracy that collapses at interval level, repeating stress windows, adherence signals that do not predict outcomes, and coverage geometry mismatch across skills, queues, or sites.',
      },
      {
        q: 'Who is this for?',
        a: 'Teams that need defensible clarity before major decisions. WFM leadership, Planning, Intraday, Ops Directors, finance partners who want evidence (not opinions), and executive stakeholders who need a coherent explanation they can act on.',
      },
      {
        q: 'Who is this NOT for?',
        a: 'If you want dashboards, BI build-outs, or ongoing managed operations. If you cannot export interval files. If you want someone to prove you need more headcount regardless of reality. If you want tool configuration, real-time integrations, or access to production systems.',
      },
      {
        q: 'Do you replace our WFM team?',
        a: 'No. We operate as a second-layer analytical function. Your team remains the decision and implementation authority.',
      },
    ],
  },
  {
    category: 'Engagement Entry: Pre-Diagnostics Session',
    questions: [
      {
        q: 'What is the Pre-Diagnostics Session?',
        a: 'A focused intake designed to confirm whether your instability is structural, execution-driven, or data-artifact driven and to produce a precise export checklist for Diagnostics.',
      },
      {
        q: 'What happens in the session?',
        a: 'We map queues, skills, channels, SLAs, operating hours, planning structure (forecast, scheduling, intraday handoffs), where instability is felt versus where it is measured, and what files exist today versus what is missing for proper classification.',
      },
      {
        q: 'What do we receive after Pre-Diagnostics?',
        a: 'A clear go/no-go recommendation for Diagnostics, a scoped export checklist (exact columns plus interval granularity plus minimum history), and a short written summary you can forward internally for procurement or leadership.',
      },
      {
        q: 'Is the Pre-Diagnostics fee refundable?',
        a: 'No. Pre-Diagnostics is a time-boxed professional service. Once scheduled and delivered, it is not refundable. Reschedules are possible under a defined notice window.',
      },
      {
        q: 'Is the Pre-Diagnostics fee credited toward Diagnostics?',
        a: 'Not automatically. Pre-Diagnostics is standalone by default; any credit or offset arrangement must be agreed separately in writing.',
      },
    ],
  },
  {
    category: 'Data, Access, and Security',
    questions: [
      {
        q: 'Do you need system access, credentials, or VPN?',
        a: 'No. We do not request credentials, VPN access, agent desktop access, or production tool access. This engagement is designed to work from exports.',
      },
      {
        q: 'What data do you require for Diagnostics?',
        a: 'Interval-level exports (15 or 30 minutes) typically including offered, handled, and abandoned volume, AHT (and ideally distribution or variance), service level or ASA by interval, staffing or scheduled or staffed counts, and queue or skill identifiers. Minimum history is usually 60 days (more is better if patterns are weekly or seasonal).',
      },
      {
        q: 'Can you work with Genesys, NICE, Verint, Calabrio, or custom?',
        a: 'Yes. The work is platform-neutral because the input is export-based.',
      },
      {
        q: 'How do you handle our data?',
        a: 'Offline processing from files only. No third-party routing or SaaS analytics chain. Data is used strictly for the engagement scope. We produce outputs (PDF memo plus annex artifacts) and then delete the data per retention policy.',
      },
      {
        q: 'What is your default retention policy?',
        a: 'Default retention is short and bounded. Files are deleted after delivery according to the retention window defined in the engagement terms. If procurement requires a different window, it can be specified.',
      },
      {
        q: 'Can you sign an NDA or DPA?',
        a: 'Yes, within reasonable procurement standards. We can operate under NDA and DPA terms aligned to handling of customer data, confidentiality, and deletion requirements.',
      },
      {
        q: 'Do you store our data permanently?',
        a: 'No. We do not retain customer operational exports as a permanent dataset. Retention is bounded and purpose-limited.',
      },
    ],
  },
  {
    category: 'Delivery Model (How Work Actually Happens)',
    questions: [
      {
        q: 'What are the steps in Diagnostics?',
        a: 'Export confirmation (we validate structure and sufficiency), classification analysis (distortion domains plus interaction drivers plus signal structure), executive memorandum plus evidence annex (delivery package), and close-out plus deletion per the retention policy.',
      },
      {
        q: 'What is structural distortion classification?',
        a: 'It is the process of separating forecast artifacts versus real demand geometry, coverage architecture mismatch versus staffing quantity, and intraday behavioral amplification versus planning errors, then mapping the recurrence and exposure of these patterns at interval level.',
      },
      {
        q: 'What do you deliver?',
        a: 'A procurement-ready output set: Executive Memorandum (PDF) with primary domain, drivers, interpretation, and bounded next steps; Evidence Annex with artifact charts, tables, heatmaps, and decomposition views; and a data sufficiency assessment stating what was enough, what was missing, and the exact export spec if needed.',
      },
      {
        q: 'Do you guarantee KPI improvements?',
        a: 'No. This is a classification and interpretation engagement. Outcomes depend on implementation quality and organizational constraints.',
      },
      {
        q: 'Do you implement changes inside our WFM tool?',
        a: 'No. We do not configure tools, deploy integrations, or operate your intraday floor. We provide structural interpretation and bounded recommendations.',
      },
    ],
  },
  {
    category: 'Commercial Terms (Clear Boundaries)',
    questions: [
      {
        q: 'Is Diagnostics priced publicly on the site?',
        a: 'No. Diagnostics is scoped. Complexity varies by environment size, queue topology, and data completeness. You receive a clear scope and written terms before work begins.',
      },
      {
        q: 'Is Diagnostics refundable?',
        a: 'As a rule, professional services are not returnable products. Refunds are not offered after work begins or after delivery. Any exceptions must be written explicitly in the engagement terms.',
      },
      {
        q: 'What triggers a re-scope?',
        a: 'We keep scope bounded. If submitted data reveals additional dimensions worth analyzing, we document them as optional follow-on modules rather than expanding the active engagement.',
      },
      {
        q: 'What counts as one environment?',
        a: 'A single operational system boundary (queue and skill topology plus scheduling system plus reporting logic). Multi-site or multi-environment organizations are typically scoped separately.',
      },
    ],
  },
  {
    category: 'Quality, Limits, and No-Go Conditions',
    questions: [
      {
        q: 'What if our exports are incomplete?',
        a: 'We do not guess. If the dataset cannot support classification, we issue a data sufficiency gap report and provide the exact export spec required for a re-engagement.',
      },
      {
        q: 'Do you do employee-level policing (discipline or investigations)?',
        a: 'No. We do not perform HR enforcement, surveillance, or disciplinary investigation work.',
      },
      {
        q: 'Do you provide legal advice?',
        a: 'No. We can provide operational documentation and data handling terms, but we do not provide legal advice or compliance decisions.',
      },
      {
        q: 'Can you work under time pressure?',
        a: 'If you need speed, we can prioritize, but only within a bounded scope. Rushing without data sufficiency creates false certainty, which is exactly what we avoid.',
      },
    ],
  },
];

/* ── FAQ spine rail — concise nav-only category aliases (owner-authorized).
   Body category headings below remain verbatim. ── */

const FAQ_NAV_LABELS = [
  'Scope & Fit',
  'Pre-Diagnostics',
  'Data & Security',
  'Delivery Model',
  'Commercial Terms',
  'Quality & Limits',
];

const FAQ_RAIL = FAQ_ITEMS.map((cat, i) => ({
  id: `faq-${i + 1}`,
  num: String(i + 1).padStart(2, '0'),
  label: FAQ_NAV_LABELS[i],
}));

const FAQ_IDS = FAQ_RAIL.map((item) => item.id);

export function FAQ() {
  const [open, setOpen] = useState({});
  const activeId = useSectionSpy(FAQ_IDS, '0px 0px -80% 0px');

  const toggle = (id) => setOpen((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="bg-zinc-950">
      <SEOHead />
      <section className="relative">
        <PageMasthead
          wide
          field="quiet"
          fieldFocal="0.82,0.18"
          eyebrow="Reference"
          title="Frequently Asked Questions"
          intro="This page explains how OnPoint.Works runs Pre-Diagnostics and OnPoint Diagnostics, how we handle data, what you receive, and what we will not do."
        />
        <div className="relative z-10">
          {/* ── Shared spine — FAQ categories (lg: nav | content) ── */}
          <div className="container-site spine-shell lg:grid lg:grid-cols-[16rem_minmax(0,1fr)]">
            <PageNav items={FAQ_RAIL} activeId={activeId} label="FAQ sections" />
            <div className="lg:min-w-0 lg:border-l lg:border-memo-line lg:pl-10">
              <MobileSectionNav items={FAQ_RAIL} activeId={activeId} label="FAQ sections" />
              <div className="space-y-12">
                {FAQ_ITEMS.map((cat, i) => (
                  <div key={cat.category} id={`faq-${i + 1}`}>
                    <h2 className="font-display text-xl md:text-2xl font-medium tracking-tight mb-4 pb-3 border-b border-memo-line">
                      {cat.category}
                    </h2>
                <div className="space-y-1">
                  {cat.questions.map((faq, i) => {
                    const id = `${cat.category}-${i}`;
                    const isOpen = !!open[id];
                    return (
                      <div key={id} data-testid={`faq-item-${id.replace(/\s+/g, '-').toLowerCase()}`} className="border border-zinc-800 rounded-md overflow-hidden">
                        <button
                          data-testid={`faq-toggle-${id.replace(/\s+/g, '-').toLowerCase()}`}
                          className="w-full text-left px-6 py-4 flex items-center justify-between gap-4 hover:bg-zinc-900/40 transition-colors duration-200"
                          onClick={() => toggle(id)}
                        >
                          <span className="text-sm font-medium text-zinc-200">{faq.q}</span>
                          {isOpen ? <ChevronUp size={15} className="text-zinc-500 flex-shrink-0" /> : <ChevronDown size={15} className="text-zinc-500 flex-shrink-0" />}
                        </button>
                        {isOpen && (
                          <div className="px-6 pb-5 border-t border-zinc-800 pt-4 bg-zinc-950">
                            <p className="text-sm text-zinc-400 leading-relaxed">{faq.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
